"use client"; // Error boundaries must be Client Components
import { AnimatedLogo } from "../components/AnimatedLogo/AnimatedLogo";
import { Button } from "../components/Button/Button";
import { Nav } from "../components/Nav/Nav";
import { SupportedLanguage } from "../config/languages";
import { detectLanguage } from "../helpers/detectLanguage";
import classNames from "classnames/bind";
import { Suspense, useEffect, useState } from "react";
// Need to import the global CSS file, since layout is in (entrix) directory to not break payload admin
import "../css/main.css";
import styles from "./500.module.css";

const cx = classNames.bind(styles);

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const [detectedLanguage, setDetectedLanguage] =
    useState<SupportedLanguage>("en");

  useEffect(() => {
    // Detect language client-side since we can't use headers in error boundaries
    const browserLanguage =
      navigator.language || navigator.languages?.[0] || "";
    const detected = detectLanguage(browserLanguage);
    setDetectedLanguage(detected);

    // Log error for monitoring
    console.error("Application error:", error);
  }, [error]);

  const messages = {
    de: {
      headline: "Etwas ist auf unserer Seite schiefgelaufen.",
      description:
        "Wir haben technische Schwierigkeiten. Bitte versuchen Sie es später erneut.",
      backToHome: "Zurück zur Startseite",
      tryAgain: "Erneut versuchen",
    },
    en: {
      headline: "Something went wrong on our end.",
      description:
        "We're experiencing technical difficulties. Please try again later.",
      backToHome: "Back to Homepage",
      tryAgain: "Try Again",
    },
  };

  const currentMessages = messages[detectedLanguage] || messages.en;

  return (
    <>
      <main className={cx("server-error")}>
        <Nav currentLang={detectedLanguage} />

        <div className={cx("content")}>
          <div className="container">
            <div className={cx("wrapper")}>
              <div className={cx("error-display")}>
                {/* Left "5" */}
                <div className={cx("digit")}>5</div>

                {/* Animated Logo as the "0" */}
                <Suspense
                  fallback={
                    <div className={cx("logo-container-fallback")}>
                      <div className={cx("logo-placeholder")}>0</div>
                    </div>
                  }
                >
                  <div className={cx("logo-container")}>
                    <AnimatedLogo />
                  </div>
                </Suspense>

                {/* Right "0" */}
                <div className={cx("digit")}>0</div>
              </div>

              <div className={cx("message")}>
                <h1 className={cx("headline")}>{currentMessages.headline}</h1>
                <p className={cx("description")}>
                  {currentMessages.description}
                </p>

                <div className={cx("actions")}>
                  <Button
                    variant="primary"
                    size="medium"
                    href={`/${detectedLanguage}`}
                    locale={detectedLanguage}
                  >
                    {currentMessages.backToHome}
                  </Button>
                  <Button
                    variant="secondary"
                    size="medium"
                    onClick={() => reset()}
                  >
                    {currentMessages.tryAgain}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
